/* eslint-disable react/prop-types */
import { createContext, useContext, useEffect, useState } from "react";

import { useConfig } from "@atlassiansox/bxp-content-framework";

import { useOnetrust } from "./use-onetrust";

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ace?: any;
    }
}

export type AnalyticsContext = {
    sendPageEvent: (name: string, payload: unknown) => void;
    sendTrackEvent: (name: string, payload: unknown) => void;
};

export const AnalyticsContext = createContext<AnalyticsContext>({
    sendPageEvent: () => {
        throw new Error("Missing provider");
    },
    sendTrackEvent: () => {
        throw new Error("Missing provider");
    }
});

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);

    if (context === undefined) {
        throw new Error(
            "`useAnalyticsContext` must be used within a `AnalyticsContext.Provider`"
        );
    }
    return context;
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const sendPageEvent = (_name: string, _payload: unknown) => undefined;
    const sendTrackEvent = (_name: string, _payload: unknown) => undefined;

    const { environment, skipAnalytics } = useConfig();
    const [trackEvents, setTrackEvents] = useState<AnalyticsContext>({
        sendPageEvent,
        sendTrackEvent
    });

    const { hasOptedOut } = useOnetrust();

    const isLocalhost =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");

    useEffect(() => {
        const scriptId = "atl-global-wrapper";
        const loadAnalyticsWrapper = () => {
            if (isLocalhost) {
                setTrackEvents({ sendPageEvent, sendTrackEvent });
                return;
            }

            const script = document.createElement("script");
            script.async = true;
            script.src =
                environment === "production"
                    ? "https://atl-global.atlassian.com/js/atl-global.min.js"
                    : "https://atl-global.stg.atlassian.com/js/atl-global.min.js";
            script.id = scriptId;

            const setWrapperEvents = () => {
                setTrackEvents({
                    sendPageEvent: (name: string, payload: unknown) => {
                        window.ace?.analytics.Tracking.pageView(name, payload);
                    },
                    sendTrackEvent: (name: string, payload: unknown) => {
                        window.ace?.analytics.Tracking.trackEvent(
                            name,
                            payload
                        );
                    }
                });
            };

            if (hasOptedOut || skipAnalytics) {
                setTrackEvents({ sendPageEvent, sendTrackEvent });
            } else {
                if (!document.getElementById(scriptId)) {
                    document.body.appendChild(script);
                    script.onload = () => {
                        window.ace?.analytics.Initializer.initAnalytics(
                            "trello"
                        );
                        setWrapperEvents();
                    };
                } else {
                    setWrapperEvents();
                }
            }
        };

        loadAnalyticsWrapper();

        if (hasOptedOut && document.getElementById(scriptId)) {
            const script = document.getElementById(scriptId);
            document.body.removeChild(script as Node);
        }
    }, [environment, hasOptedOut, isLocalhost, skipAnalytics]);

    return (
        <AnalyticsContext.Provider value={{ ...trackEvents }}>
            {children}
        </AnalyticsContext.Provider>
    );
};
